// Storybook Vault (Parent Dashboard): gallery of recorded audiobooks with
// Collector's Edition highlighting, filters, inline player, MP3 + cover
// downloads, share links, and a 30-day Recently Deleted bin (undo toast,
// restore, countdown, Delete Now, expiring-soon amber banner). Notification
// guard: deletions never send emails — reminders live here + the weekly
// digest footnote only.
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, BookAudio, Clock, Download, Headphones, Image as ImageIcon, KeyRound, Link2, ListMusic, Play, Plus, RotateCcw, Square, Star, Trash2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { downloadPng, renderCoverPng } from "../../lib/coverArt";
import { CoverCard } from "./CoverCard";
import { ShareModal } from "./ShareModal";

const FILTERS = [
  ["all", "All Stories"],
  ["special", "Special Editions"],
  ["three", "3-Star Reads"],
  ["trash", "Recently Deleted"],
];
const safeName = (t) => (t || "story").replace(/[^a-z0-9 _-]/gi, "").trim() || "story";

const StarRow = ({ n }) => (
  n > 0 ? (
    <span className="flex items-center gap-0.5" aria-label={`${n} star read`}>
      {[1, 2, 3].map((k) => (
        <Star key={k} className={`h-4 w-4 ${n >= k ? "fill-amber-400 text-amber-500" : "text-muted-foreground/40"}`} aria-hidden="true" />
      ))}
    </span>
  ) : (
    <span className="text-[11px] font-bold text-muted-foreground" data-testid="vault-not-scored">Not scored yet</span>
  )
);

export const StorybookVault = () => {
  const [books, setBooks] = useState(null);
  const [filter, setFilter] = useState("all");
  const [playing, setPlaying] = useState(null); // {id, url}
  const [shareBook, setShareBook] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [plName, setPlName] = useState("");
  const [plDraft, setPlDraft] = useState([]); // book_ids being grouped
  const [seq, setSeq] = useState(null); // sequential playlist player
  const seqUrl = useRef(null);
  const urlRef = useRef(null);

  const loadPlaylists = () => api.get("/vault/playlists").then(({ data }) => setPlaylists(data.playlists || [])).catch(() => {});
  const load = () => {
    loadPlaylists();
    return api.get("/vault/list").then(({ data }) => setBooks(data.books)).catch(() => setBooks([]));
  };
  useEffect(() => {
    load();
    return () => urlRef.current && URL.revokeObjectURL(urlRef.current);
  }, []);

  if (books === null) return <p className="text-sm text-muted-foreground p-4">Loading the vault...</p>;
  const active = books.filter((b) => !b.is_deleted);
  const trash = books.filter((b) => b.is_deleted);
  const expiring = trash.filter((b) => (b.days_left ?? 99) <= 3);
  const shown = filter === "trash" ? trash
    : active.filter((b) => (filter === "special" ? b.has_rare : filter === "three" ? b.stars === 3 : true));

  const stopAudio = () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
    setPlaying(null);
  };
  const play = async (b) => {
    if (playing?.id === b.book_id) return stopAudio();
    try {
      const r = await api.get(`/vault/${b.book_id}/audio`, { responseType: "blob" });
      stopAudio();
      const url = URL.createObjectURL(r.data);
      urlRef.current = url;
      setPlaying({ id: b.book_id, url });
    } catch { toast.error("Could not load the recording."); }
  };
  const dlMp3 = async (b) => {
    try {
      const r = await api.get(`/vault/${b.book_id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${safeName(b.title)}.mp3`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Download failed. Try again."); }
  };
  const dlCover = async (b) => downloadPng(await renderCoverPng(b.cover), `${safeName(b.title)}-cover.png`);
  const revokeLink = async (b) => {
    await api.delete(`/vault/${b.book_id}/share`).catch(() => {});
    toast.success("Public link revoked — the QR code is now inactive.");
    load();
  };
  const restore = async (b) => {
    await api.post(`/vault/${b.book_id}/restore`).catch(() => {});
    toast.success(`"${b.title}" restored to the vault`);
    load();
  };
  const del = async (b) => {
    if (playing?.id === b.book_id) stopAudio();
    await api.delete(`/vault/${b.book_id}`).catch(() => {});
    load();
    toast(`"${b.title}" moved to Recently Deleted`, {
      description: "It will be permanently removed in 30 days.",
      action: { label: "Undo", onClick: () => restore(b) },
    });
  };
  const purgeNow = async (b) => {
    // Confirm step: permanent deletion is unrecoverable.
    if (!window.confirm(`Permanently delete "${b.title}"? This cannot be undone.`)) return;
    await api.delete(`/vault/${b.book_id}/purge`).catch(() => {});
    toast.success("Permanently deleted");
    load();
  };

  // --- Bedtime playlists: build from cards, play one story after another --
  const addToDraft = (b) => setPlDraft((d) => (d.includes(b.book_id) ? d : [...d, b.book_id]));
  const savePlaylist = async () => {
    try {
      await api.post("/vault/playlists", { name: plName.trim(), book_ids: plDraft });
      setPlName(""); setPlDraft([]);
      toast.success("Playlist saved!");
      loadPlaylists();
    } catch { toast.error("Could not save the playlist."); }
  };
  const delPlaylist = async (pl) => {
    await api.delete(`/vault/playlists/${pl.playlist_id}`).catch(() => {});
    if (seq?.pid === pl.playlist_id) stopSeq();
    loadPlaylists();
  };
  const stopSeq = () => {
    if (seqUrl.current) URL.revokeObjectURL(seqUrl.current);
    seqUrl.current = null;
    setSeq(null);
  };
  const seqNext = async (pl, idx) => {
    if (idx >= pl.book_ids.length) { stopSeq(); toast.success("Playlist finished — sweet dreams!"); return; }
    const b = books.find((x) => x.book_id === pl.book_ids[idx] && !x.is_deleted);
    if (!b) return seqNext(pl, idx + 1); // skip trashed/missing stories
    try {
      const r = await api.get(`/vault/${b.book_id}/audio`, { responseType: "blob" });
      if (seqUrl.current) URL.revokeObjectURL(seqUrl.current);
      const url = URL.createObjectURL(r.data);
      seqUrl.current = url;
      setSeq({ pid: pl.playlist_id, idx, url, title: b.title, total: pl.book_ids.length, pl });
    } catch { seqNext(pl, idx + 1); }
  };

  return (
    <div className="space-y-5" data-testid="storybook-vault">
      {expiring.length > 0 && (
        <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/40 p-4 flex flex-wrap items-center gap-3" data-testid="vault-expiring-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-48">
            <p className="text-sm font-bold text-amber-900 dark:text-amber-200">
              {expiring.length === 1
                ? `"${expiring[0].title}" will be permanently deleted in ${expiring[0].days_left} day${expiring[0].days_left === 1 ? "" : "s"}.`
                : `${expiring.length} stories will be permanently deleted within 3 days: ${expiring.slice(0, 3).map((b) => `"${b.title}"`).join(", ")}.`}
            </p>
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setFilter("trash")} data-testid="vault-banner-review">Review</Button>
          <Button size="sm" className="rounded-full" onClick={() => expiring.forEach(restore)} data-testid="vault-banner-restore">
            Restore{expiring.length > 1 ? " all" : ""}
          </Button>
        </div>
      )}

      {(plDraft.length > 0 || playlists.length > 0 || active.length > 1) && (
        <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="vault-playlists">
          <p className="flex items-center gap-1.5 text-sm font-extrabold">
            <ListMusic className="h-4 w-4 text-primary" aria-hidden="true" /> Bedtime playlists
          </p>
          {plDraft.length === 0 && playlists.length === 0 && (
            <p className="text-xs text-muted-foreground">Tap "+ List" on story cards to build a playlist that plays one story after another.</p>
          )}
          {plDraft.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {plDraft.map((id) => (
                <span key={id} className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-bold" data-testid="playlist-draft-chip">
                  {books.find((b) => b.book_id === id)?.title || id}
                  <button type="button" className="ml-1.5 font-extrabold" onClick={() => setPlDraft(plDraft.filter((x) => x !== id))} aria-label="Remove from playlist">×</button>
                </span>
              ))}
              <input value={plName} onChange={(e) => setPlName(e.target.value)} maxLength={40}
                placeholder="Playlist name..." aria-label="Playlist name"
                className="rounded-full border border-border bg-background px-3 py-1.5 text-sm font-bold" data-testid="playlist-name-input" />
              <Button size="sm" className="rounded-full" disabled={!plName.trim()} onClick={savePlaylist} data-testid="playlist-save-btn">Save playlist</Button>
            </div>
          )}
          {playlists.map((pl) => (
            <div key={pl.playlist_id} className="flex flex-wrap items-center gap-2" data-testid={`playlist-row-${pl.playlist_id}`}>
              <span className="flex-1 text-sm font-bold">{pl.name} · {pl.book_ids.length} stor{pl.book_ids.length === 1 ? "y" : "ies"}</span>
              <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => (seq?.pid === pl.playlist_id ? stopSeq() : seqNext(pl, 0))} data-testid="playlist-play-btn">
                {seq?.pid === pl.playlist_id ? <Square className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
                {seq?.pid === pl.playlist_id ? "Stop" : "Play all"}
              </Button>
              <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => delPlaylist(pl)} aria-label="Delete playlist" data-testid="playlist-delete-btn">
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          ))}
          {seq && (
            <div className="space-y-1" data-testid="playlist-now-playing">
              <p className="text-xs font-bold">Now playing {seq.idx + 1} of {seq.total}: {seq.title}</p>
              <audio controls autoPlay src={seq.url} className="w-full h-9" onEnded={() => seqNext(seq.pl, seq.idx + 1)} data-testid="playlist-audio" />
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="vault-filters">
        {FILTERS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k)}
            className={`rounded-full px-4 py-1.5 text-sm font-bold border transition-colors ${filter === k ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}
            data-testid={`vault-filter-${k}`}>
            {label}{k === "trash" && trash.length > 0 ? ` (${trash.length})` : ""}
          </button>
        ))}
      </div>

      {shown.length === 0 && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center" data-testid="vault-empty">
          <BookAudio className="h-10 w-10 mx-auto text-muted-foreground mb-3" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            {filter === "trash" ? "Nothing in Recently Deleted." : "No recorded stories here yet — your child can record one in Pip's Story Studio!"}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {shown.map((b) => (
          <div key={b.book_id}
            className={`relative rounded-2xl border bg-card p-3 space-y-2 ${b.has_rare ? "border-amber-400 border-2" : "border-border"}`}
            data-testid={`vault-card-${b.book_id}`}>
            {b.has_rare && (
              <span className="collectors-badge absolute -top-2.5 left-3 z-10 rounded-full bg-amber-400 text-amber-950 text-[10px] font-extrabold px-2.5 py-0.5 uppercase tracking-wide" data-testid="collectors-edition-badge">
                Collector's Edition
              </span>
            )}
            <CoverCard cover={b.cover} />
            <div className="space-y-0.5 px-1">
              <p className="font-extrabold font-display text-base truncate" data-testid="vault-card-title">{b.title}</p>
              <p className="text-xs text-muted-foreground">
                {b.child_name ? `${b.child_name} · ` : ""}{new Date(b.created_at).toLocaleDateString()} · {b.grade_band}
              </p>
              <div className="flex items-center justify-between">
                <StarRow n={b.stars || 0} />
                {b.is_deleted && (
                  <span className="flex items-center gap-1 text-[11px] font-bold text-amber-600" data-testid="vault-expires-chip">
                    <Clock className="h-3.5 w-3.5" aria-hidden="true" /> Expires in {b.days_left} day{b.days_left === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              {!b.is_deleted && (b.listen_count > 0 || b.share_token) && (
                <p className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="vault-listen-log">
                  <Headphones className="h-3 w-3" aria-hidden="true" /> Listened {b.listen_count || 0} time{(b.listen_count || 0) === 1 ? "" : "s"}{b.last_listened_at ? ` · Last listened: ${new Date(b.last_listened_at).toLocaleString()}` : ""}
                </p>
              )}
            </div>
            {b.is_deleted ? (
              <div className="flex gap-2 px-1 pb-1">
                <Button size="sm" className="rounded-full flex-1 gap-1" onClick={() => restore(b)} data-testid="vault-restore-btn">
                  <RotateCcw className="h-4 w-4" aria-hidden="true" /> Restore
                </Button>
                <Button size="sm" variant="destructive" className="rounded-full flex-1 gap-1" onClick={() => purgeNow(b)} data-testid="vault-purge-btn">
                  <Trash2 className="h-4 w-4" aria-hidden="true" /> Delete Now
                </Button>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5 px-1 pb-1">
                  <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => play(b)} data-testid="vault-play-btn">
                    {playing?.id === b.book_id ? <Square className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
                    {playing?.id === b.book_id ? "Stop" : "Play"}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => dlMp3(b)} data-testid="vault-download-btn">
                    <Download className="h-4 w-4" aria-hidden="true" /> MP3
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => dlCover(b)} data-testid="vault-cover-btn">
                    <ImageIcon className="h-4 w-4" aria-hidden="true" /> Cover
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => setShareBook(b)} data-testid="vault-share-btn">
                    <Link2 className="h-4 w-4" aria-hidden="true" /> Share
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => addToDraft(b)} data-testid="vault-add-playlist-btn">
                    <Plus className="h-4 w-4" aria-hidden="true" /> List
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full gap-1 text-destructive" onClick={() => del(b)} data-testid="vault-delete-btn">
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
                {b.share_token && b.share_enabled && (
                  <div className="flex gap-1.5 px-1 pb-1">
                    <Button size="sm" variant="outline" className="rounded-full flex-1 gap-1 text-xs" onClick={() => setShareBook(b)} data-testid="vault-change-pin-btn">
                      <KeyRound className="h-3.5 w-3.5" aria-hidden="true" /> Change PIN
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full flex-1 gap-1 text-xs text-destructive" onClick={() => revokeLink(b)} data-testid="vault-revoke-btn">
                      <Unlink className="h-3.5 w-3.5" aria-hidden="true" /> Revoke Link
                    </Button>
                  </div>
                )}
                {playing?.id === b.book_id && (
                  <audio controls autoPlay src={playing.url} className="w-full h-9" data-testid="vault-audio-player" />
                )}
              </>
            )}
          </div>
        ))}
      </div>
      <ShareModal book={shareBook} onClose={() => setShareBook(null)} onChanged={load} />
    </div>
  );
};
